import { useState, useEffect, useCallback } from "react";
import companyCapitalService from "../services/companyCapitalService";

const DEFAULT_FILTERS = {
  Type: "",
  From: "",
  To: "",
  SearchTerm: "",
  PageIndex: 1,
  PageSize: 10,
};

const DEFAULT_SUMMARY = { totalCash: 0, totalAssets: 0, totalCapital: 0 };

const useCompanyCapital = () => {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(DEFAULT_SUMMARY);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [pagination, setPagination] = useState({
    totalCount: 0,
    totalPages: 1,
    currentPage: 1,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await companyCapitalService.getAll(filters);
      setItems(res?.data || []);
      setPagination({
        totalCount: res?.totalCount || 0,
        totalPages: res?.totalPages || 1,
        currentPage: res?.currentPage || 1,
        hasNextPage: res?.hasNextPage || false,
        hasPreviousPage: res?.hasPreviousPage || false,
      });
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await companyCapitalService.getSummary();
      setSummary(res?.data || DEFAULT_SUMMARY);
    } catch (err) {
      console.error("فشل جلب ملخص رأس المال:", err);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const updateFilters = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters, PageIndex: 1 }));
  };

  const goToPage = (page) => {
    setFilters((prev) => ({ ...prev, PageIndex: page }));
  };

  const addItem = async (payload, setFieldError) => {
    const created = await companyCapitalService.create(payload, setFieldError);
    if (created) await Promise.all([fetchList(), fetchSummary()]);
    return created;
  };

  const editItem = async (id, payload, setFieldError) => {
    const updated = await companyCapitalService.update(id, payload, setFieldError);
    if (updated) await Promise.all([fetchList(), fetchSummary()]);
    return updated;
  };

  const deleteItem = async (id) => {
    await companyCapitalService.remove(id);
    await Promise.all([fetchList(), fetchSummary()]);
  };

  return {
    items,
    summary,
    filters,
    pagination,
    loading,
    error,
    updateFilters,
    goToPage,
    addItem,
    editItem,
    deleteItem,
    refetch: fetchList,
  };
};

export default useCompanyCapital;